import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch,
  FaSpinner,
  FaDownload,
  FaShare,
  FaTrophy,
  FaGraduationCap,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaArrowLeft,
  FaSync,
  FaGlobe,
  FaShieldAlt,
  FaRocket,
  FaUserGraduate,
  FaFileAlt,
  FaCalendarAlt,
  FaMedal,
  FaCrown,
  FaAward,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import confetti from "canvas-confetti";
import { useFooter } from "../context/FooterContext";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const ResultChecker = () => {
  const [step, setStep] = useState(1);
  const [usn, setUsn] = useState("");
  const [examType, setExamType] = useState("Regular");
  const [captcha, setCaptcha] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [showCaptchaInput, setShowCaptchaInput] = useState(false);
  const [captchaImage, setCaptchaImage] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [activeUrl, setActiveUrl] = useState(null);
  const [captchaError, setCaptchaError] = useState("");
  const [showCelebration, setShowCelebration] = useState(false);
  const [clearedSubjects, setClearedSubjects] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { setShowFooter } = useFooter();

  // Hide footer
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    fetchActiveUrl();
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const fetchActiveUrl = async (selectedExamType = "Regular") => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/results/active-url?examType=BE&urlType=${selectedExamType}`,
      );
      setActiveUrl(response.data);
    } catch (error) {
      console.error("Failed to fetch active URL:", error);
    }
  };

  useEffect(() => {
    if (examType) {
      fetchActiveUrl(examType);
    }
  }, [examType]);

  const handleUsnChange = (e) => {
    let value = e.target.value.toUpperCase();
    if (value.length <= 10) {
      setUsn(value);
    }
  };

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/results/captcha?examType=${examType}`,
      );
      if (response.data.captchaImage) {
        setCaptchaImage(response.data.captchaImage);
      } else {
        setCaptchaImage(null);
      }
      setSessionId(response.data.sessionId);
    } catch (error) {
      toast.error("Failed to load CAPTCHA");
      console.error("Captcha fetch error:", error);
    } finally {
      setCaptchaLoading(false);
    }
  };

  const checkForClearedBacklogs = (resultData) => {
    if (resultData?.examType !== "Revaluation") return;
    const cleared = resultData.subjects.filter(
      (sub) => sub.oldResult === "F" && sub.result === "P",
    );
    if (cleared.length > 0) {
      setClearedSubjects(cleared);
      setShowCelebration(true);
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#8b5cf6", "#10b981", "#f59e0b", "#3b82f6"],
      });
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 100,
          origin: { y: 0.5, x: 0.3 },
          colors: ["#8b5cf6", "#10b981"],
        });
      }, 300);
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 100,
          origin: { y: 0.5, x: 0.7 },
          colors: ["#f59e0b", "#3b82f6"],
        });
      }, 600);
      setTimeout(() => {
        setShowCelebration(false);
      }, 5000);
    }
  };

  const handleFetchClick = async () => {
    if (!usn.trim()) {
      toast.error("Please enter USN");
      return;
    }
    await fetchCaptcha();
    setShowCaptchaInput(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCaptchaError("");
    const usnRegex = /^[1-4][A-Z]{2}\d{2}[A-Z]{2}\d{3}$/i;
    if (!usn.trim()) {
      toast.error("Please enter USN");
      return;
    }
    if (usn.length !== 10) {
      toast.error("USN must be exactly 10 characters long (e.g., XXX00XX000)");
      return;
    }
    if (!usnRegex.test(usn)) {
      toast.error("Invalid USN format. Use format: 1BM21CS001 (10 characters)");
      return;
    }
    if (!captcha.trim()) {
      toast.error("Please enter CAPTCHA");
      return;
    }
    // ADD CAPTCHA LENGTH VALIDATION HERE
    if (captcha.length !== 6) {
      toast.error("CAPTCHA must be exactly 6 characters");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API_BASE_URL}/api/results/fetch`, {
        usn,
        examType,
        captcha,
        sessionId,
      });
      setResult(response.data.result);
      setStep(2);
      checkForClearedBacklogs(response.data.result);
      toast.success(
        response.data.fromCache
          ? "Result fetched from cache!"
          : "Result fetched successfully!",
      );
    } catch (err) {
      let errorMessage = "Failed to fetch result";
      if (err.response) {
        if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.status === 400) {
          errorMessage = "Invalid USN or CAPTCHA. Please check and try again.";
        } else if (err.response.status === 429) {
          errorMessage =
            "Too many requests. Please wait 30 seconds before trying again.";
        } else if (err.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        }
      } else if (err.request) {
        errorMessage = "No response from server. Please check your connection.";
      } else {
        errorMessage = err.message;
      }
      setCaptchaError(errorMessage);
      setError(errorMessage);
      toast.error(errorMessage);
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  // Group subjects by semester
  const groupSubjectsBySemester = (subjects) => {
    const groups = {};
    subjects?.forEach((subject) => {
      let semester = 0;
      const match = subject.subjectCode.match(/\d{3}(\d)/);
      if (match && match[1]) {
        semester = parseInt(match[1]);
      } else {
        const semMatch = subject.subjectCode.match(/(\d)/);
        if (semMatch) {
          semester = parseInt(semMatch[1]);
        }
      }
      if (!groups[semester]) {
        groups[semester] = [];
      }
      groups[semester].push(subject);
    });
    return groups;
  };

  const hasFailedSubjects = (subjects) => {
    return subjects?.some((subject) => subject.result === "F");
  };

  const getOverallResultStatus = (subjects) => {
    if (!subjects || subjects.length === 0) return "PENDING";
    return hasFailedSubjects(subjects) ? "FAIL" : "PASS";
  };

  const downloadResult = async () => {
    const resultElement = document.getElementById("result-card");
    if (resultElement) {
      try {
        const cloneElement = resultElement.cloneNode(true);
        const buttonsContainer = cloneElement.querySelector(".action-buttons");
        if (buttonsContainer) {
          buttonsContainer.remove();
        }
        const subjects = result?.subjects || [];
        const groupedSubjects = groupSubjectsBySemester(subjects);
        const sortedSemesters = Object.keys(groupedSubjects).sort(
          (a, b) => b - a,
        );
        const totalMarks = subjects.reduce((sum, s) => sum + s.total, 0);
        const overallResult = getOverallResultStatus(subjects);
        const printWindow = window.open("", "_blank");
        let subjectsHtml = "";
        for (const semester of sortedSemesters) {
          const semSubjects = groupedSubjects[semester];
          subjectsHtml += `
            <div style="margin-bottom: 30px;">
              <h4 style="background: linear-gradient(135deg, #8b5cf6, #3b82f6); color: white; padding: 8px 12px; border-radius: 8px; margin-bottom: 15px;">
                Semester ${semester} Subjects
              </h4>
              <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
                <thead>
                  <tr style="background: #f8f9fa;">
                    <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Subject Code</th>
                    <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Subject Name</th>
                    <th style="border: 1px solid #ddd; padding: 8px; text-align: center;">Internal</th>
                    <th style="border: 1px solid #ddd; padding: 8px; text-align: center;">External</th>
                    <th style="border: 1px solid #ddd; padding: 8px; text-align: center;">Total</th>
                    <th style="border: 1px solid #ddd; padding: 8px; text-align: center;">Result</th>
                  </tr>
                </thead>
                <tbody>
                  ${semSubjects
                    .map(
                      (subject) => `
                      <tr>
                        <td style="border: 1px solid #ddd; padding: 8px;">${subject.subjectCode}</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">${subject.subjectName}</td>
                        <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${subject.internal}</td>
                        <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${subject.external}</td>
                        <td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-weight: bold;">${subject.total}</td>
                        <td style="border: 1px solid #ddd; padding: 8px; text-align: center; color: ${subject.result === "P" ? "#10b981" : "#ef4444"}; font-weight: bold;">${subject.result}</td>
                      </tr>
                    `,
                    )
                    .join("")}
                </tbody>
                <tfoot>
                  <tr style="background: #f8f9fa;">
                    <td colspan="5" style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold;">Semester Total:</td>
                    <td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-weight: bold;">${semSubjects.reduce((sum, s) => sum + s.total, 0)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          `;
        }
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>VTU Result - ${result?.usn || "Student"}</title>
              <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; background: #f5f5f5; }
                .result-container { max-width: 1200px; margin: 0 auto; background: white; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); overflow: hidden; }
                .header { background: linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%); padding: 30px; text-align: center; color: white; }
                .header h2 { font-size: 28px; margin-bottom: 8px; }
                .student-info { padding: 25px 30px; background: #f8f9fa; border-bottom: 1px solid #e0e0e0; }
                .info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; }
                .rank-section { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; padding: 20px 30px; background: white; border-bottom: 1px solid #e0e0e0; }
                .rank-card { text-align: center; padding: 15px; background: #f8f9fa; border-radius: 10px; }
                .rank-card .label { font-size: 12px; color: #666; margin-bottom: 8px; }
                .rank-card .value { font-size: 28px; font-weight: bold; color: #8b5cf6; }
                .footer { padding: 20px 30px; text-align: center; border-top: 1px solid #e0e0e0; font-size: 11px; color: #999; }
                @media print { body { padding: 0; background: white; } .result-container { box-shadow: none; } }
              </style>
            </head>
            <body>
              <div class="result-container">
                <div class="header">
                  <h2>VTUGenX</h2>
                  <p>Provisional Result Statement</p>
                  <p style="font-size: 12px; margin-top: 8px;">${result?.examMonth || "December"}-${result?.examYear || "2025"} / ${result?.examMonth ? parseInt(result.examYear) + 1 : "2026"}</p>
                </div>
                <div class="student-info">
                  <h3>Student Information</h3>
                  <div class="info-grid">
                    <div><span class="label">Student Name</span><br/><strong>${result?.name || "N/A"}</strong></div>
                    <div><span class="label">USN</span><br/><strong>${result?.usn || "N/A"}</strong></div>
                    <div><span class="label">Semester</span><br/><strong>${result?.semester || "N/A"}th Semester</strong></div>
                  </div>
                </div>
                <div class="rank-section">
                  <div class="rank-card"><div class="label">Result Status</div><div class="status" style="color: ${overallResult === "PASS" ? "#10b981" : "#ef4444"}; font-size: 24px; font-weight: bold;">${overallResult}</div></div>
                  <div class="rank-card"><div class="label">Class Rank</div><div class="value">${result?.classRank || "N/A"}</div></div>
                  <div class="rank-card"><div class="label">University Rank</div><div class="value">${result?.universityRank || "N/A"}</div></div>
                </div>
                <div style="padding: 25px 30px;">
                  <h3>Subject-wise Performance</h3>
                  ${subjectsHtml}
                  <div style="margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px; text-align: right;">
                    <strong>Grand Total: ${totalMarks} marks</strong>
                  </div>
                </div>
                <div class="footer">
                  <p>Generated on: ${new Date().toLocaleString()}</p>
                  <p>© VTUGenX</p>
                </div>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
          };
        };
        toast.success("PDF generation successful! Print dialog opened.");
      } catch (error) {
        console.error("PDF generation error:", error);
        toast.error("Failed to generate PDF");
      }
    }
  };

  const shareResult = () => {
    if (!result) return;
    const groupedSubjects = groupSubjectsBySemester(result.subjects);
    const sortedSemesters = Object.keys(groupedSubjects).sort((a, b) => b - a);
    const overallResult = getOverallResultStatus(result.subjects);
    let subjectsText = "";
    for (const semester of sortedSemesters) {
      subjectsText += `\n\n📚 Semester ${semester}:\n`;
      groupedSubjects[semester].forEach((s) => {
        subjectsText += `  ${s.subjectCode}: ${s.total} marks (${s.result})\n`;
      });
      const semTotal = groupedSubjects[semester].reduce(
        (sum, s) => sum + s.total,
        0,
      );
      subjectsText += `  Semester Total: ${semTotal} marks\n`;
    }
    const text = `📊 VTU Result\n\nStudent: ${result.name}\nUSN: ${result.usn}\nCurrent Semester: ${result.semester}\nOverall Result: ${overallResult}\n\nClass Rank: ${result.classRank || "N/A"}\nUniversity Rank: ${result.universityRank || "N/A"}${subjectsText}\n\nGrand Total: ${result.subjects.reduce((sum, s) => sum + s.total, 0)} marks`;
    if (navigator.share) {
      navigator
        .share({
          title: "VTU Result",
          text: text,
          url: window.location.href,
        })
        .catch(() => {
          navigator.clipboard.writeText(text);
          toast.success("Copied to clipboard!");
        });
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    }
  };

  const resetForm = () => {
    setStep(1);
    setResult(null);
    setUsn("");
    setCaptcha("");
    setExamType("Regular");
    setShowCaptchaInput(false);
    setError("");
    setCaptchaImage("");
    setSessionId("");
    setShowCelebration(false);
    setClearedSubjects([]);
  };

  if (step === 1) {
    return (
      <div
        style={{
          minHeight: "calc(100vh - 200px)",
          background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
          padding: isMobile ? "20px 16px" : "40px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 0,
            backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          style={{
            position: "fixed",
            top: "10%",
            left: "-5%",
            width: "250px",
            height: "250px",
            background:
              "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
            borderRadius: "50%",
            filter: "blur(60px)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <motion.div
          animate={{ x: [0, -100, 0], y: [0, 80, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          style={{
            position: "fixed",
            bottom: "10%",
            right: "-5%",
            width: "300px",
            height: "300px",
            background:
              "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
            borderRadius: "50%",
            filter: "blur(60px)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <div
          style={{
            maxWidth: "500px",
            width: "100%",
            position: "relative",
            zIndex: 1,
          }}
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "32px",
              padding: isMobile ? "24px" : "32px",
              border: `1px solid ${getBorderColor()}`,
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.25)",
              textAlign: "center",
            }}
          >
            <motion.div
              animate={{
                background: [
                  "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4, #8b5cf6)",
                  "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)",
                ],
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                borderRadius: "32px 32px 0 0",
              }}
            />
            <motion.div
              variants={itemVariants}
              style={{ position: "absolute", top: "1.5rem", left: "1.5rem" }}
            >
              <Link
                to="/dashboard"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  color: "#8b5cf6",
                  textDecoration: "none",
                  fontSize: "0.8rem",
                  padding: "0.3rem 0.6rem",
                  borderRadius: "8px",
                  background: isDarkMode
                    ? "rgba(139,92,246,0.1)"
                    : "rgba(139,92,246,0.05)",
                }}
              >
                <FaArrowLeft size={12} /> Back
              </Link>
            </motion.div>
            <motion.div variants={itemVariants}>
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    width: "80px",
                    height: "80px",
                    borderRadius: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 20px 40px -12px rgba(139,92,246,0.4)",
                  }}
                >
                  <FaUserGraduate size={40} color="white" />
                </div>
              </motion.div>
            </motion.div>
            <motion.h1
              variants={itemVariants}
              style={{
                fontSize: isMobile ? "24px" : "28px",
                fontWeight: "bold",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                marginBottom: "8px",
              }}
            >
              VTUGenX
            </motion.h1>
            <motion.p
              variants={itemVariants}
              style={{ color: getTextSecondary(), marginBottom: "16px" }}
            >
              Get Your Results Instantly
            </motion.p>
            {activeUrl && (
              <motion.div
                variants={itemVariants}
                style={{
                  marginTop: "8px",
                  padding: "6px 12px",
                  background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f8f9fa",
                  borderRadius: "20px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "11px",
                  color: getTextSecondary(),
                }}
              >
                <FaGlobe size={10} />
                {activeUrl.name}
              </motion.div>
            )}
            <motion.div
              variants={itemVariants}
              style={{ marginTop: "24px", marginBottom: "24px" }}
            >
              <label
                style={{
                  display: "block",
                  textAlign: "left",
                  color: getTextSecondary(),
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Enter USN
              </label>
              <input
                type="text"
                value={usn}
                onChange={handleUsnChange}
                placeholder="Enter USN"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "16px",
                  fontSize: "16px",
                  textTransform: "uppercase",
                  outline: "none",
                  transition: "all 0.3s",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
              />
            </motion.div>
            <motion.div
              variants={itemVariants}
              style={{ marginBottom: "24px" }}
            >
              <label
                style={{
                  display: "block",
                  textAlign: "left",
                  color: getTextSecondary(),
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Exam Type
              </label>
              <div style={{ display: "flex", gap: "12px" }}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => setExamType("Regular")}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background:
                      examType === "Regular"
                        ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                        : isDarkMode
                          ? "rgba(139,92,246,0.1)"
                          : "#f8f9fa",
                    color:
                      examType === "Regular" ? "white" : getTextSecondary(),
                    border:
                      examType === "Regular"
                        ? "none"
                        : `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "500",
                  }}
                >
                  Regular
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => setExamType("Revaluation")}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background:
                      examType === "Revaluation"
                        ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                        : isDarkMode
                          ? "rgba(139,92,246,0.1)"
                          : "#f8f9fa",
                    color:
                      examType === "Revaluation" ? "white" : getTextSecondary(),
                    border:
                      examType === "Revaluation"
                        ? "none"
                        : `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "500",
                  }}
                >
                  Revaluation
                </motion.button>
              </div>
            </motion.div>
            <motion.button
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleFetchClick}
              disabled={captchaLoading}
              style={{
                width: "100%",
                padding: "14px",
                background: captchaLoading
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                color: "white",
                border: "none",
                borderRadius: "16px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: captchaLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                boxShadow: captchaLoading
                  ? "none"
                  : "0 4px 14px 0 rgba(139,92,246,0.4)",
              }}
            >
              {captchaLoading ? (
                <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <FaSearch />
              )}
              {captchaLoading ? "Loading CAPTCHA..." : "Fetch Result"}
            </motion.button>
            <motion.div
              variants={itemVariants}
              style={{ marginTop: "20px", textAlign: "center" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                <FaShieldAlt size={12} color="#10b981" />
                <p style={{ color: getTextSecondary(), fontSize: "11px" }}>
                  Secure result fetching with CAPTCHA verification
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
        {showCaptchaInput && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              backdropFilter: "blur(4px)",
            }}
            onClick={() => setShowCaptchaInput(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "28px",
                padding: isMobile ? "24px" : "32px",
                maxWidth: "450px",
                width: "90%",
                textAlign: "center",
                border: `1px solid ${getBorderColor()}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginBottom: "16px", color: getTextColor() }}>
                Enter CAPTCHA
              </h2>
              <p
                style={{
                  color: getTextSecondary(),
                  marginBottom: "20px",
                  fontSize: "14px",
                }}
              >
                <FaExclamationTriangle
                  style={{
                    display: "inline",
                    marginRight: "4px",
                    color: "#f59e0b",
                  }}
                />
                Make sure the CAPTCHA is correct.
              </p>
              <div
                style={{
                  background: isDarkMode ? "rgba(0,0,0,0.3)" : "#f0f0f0",
                  borderRadius: "16px",
                  padding: "16px",
                  marginBottom: "20px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {captchaLoading ? (
                  <FaSpinner
                    style={{
                      animation: "spin 1s linear infinite",
                      fontSize: "24px",
                      color: "#8b5cf6",
                    }}
                  />
                ) : captchaImage ? (
                  <img
                    src={captchaImage}
                    alt="CAPTCHA"
                    style={{ maxWidth: "100%", borderRadius: "8px" }}
                  />
                ) : (
                  <p style={{ color: getTextSecondary() }}>
                    Loading CAPTCHA...
                  </p>
                )}
              </div>
              <input
                type="text"
                value={captcha}
                onChange={(e) => setCaptcha(e.target.value)}
                placeholder="Enter CAPTCHA"
                maxLength="6"
                style={{
                  width: "100%",
                  padding: "14px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "16px",
                  fontSize: "16px",
                  textAlign: "center",
                  outline: "none",
                  marginBottom: "20px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
                autoFocus
              />
              <div
                style={{ display: "flex", gap: "12px", marginBottom: "12px" }}
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowCaptchaInput(false);
                    setCaptcha("");
                  }}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f8f9fa",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    cursor: "pointer",
                    color: getTextColor(),
                  }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: loading
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {loading ? (
                    <FaSpinner
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                  ) : (
                    <FaSearch />
                  )}
                  Verify & Fetch
                </motion.button>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={fetchCaptcha}
                style={{
                  width: "100%",
                  padding: "10px",
                  background: "none",
                  border: "none",
                  color: "#8b5cf6",
                  cursor: "pointer",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                }}
              >
                <FaSync /> Refresh CAPTCHA
              </motion.button>
              {error && (
                <p
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    marginTop: "12px",
                  }}
                >
                  {error}
                </p>
              )}
              {captchaError && (
                <p
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    marginTop: "12px",
                    textAlign: "center",
                  }}
                >
                  {captchaError}
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </div>
    );
  }

  const groupedSubjects = result
    ? groupSubjectsBySemester(result.subjects)
    : {};
  const sortedSemesters = Object.keys(groupedSubjects).sort((a, b) => b - a);
  const overallResult = getOverallResultStatus(result?.subjects);

  // Use the exact rank values from the API response - NO modification
  const classRankValue = result?.classRank;
  const universityRankValue = result?.universityRank;

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        padding: isMobile ? "20px 16px" : "40px 20px",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -5 }}
          onClick={resetForm}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: isDarkMode
              ? "rgba(139,92,246,0.1)"
              : "rgba(139,92,246,0.05)",
            border: `1px solid ${getBorderColor()}`,
            color: "#8b5cf6",
            cursor: "pointer",
            marginBottom: "20px",
            fontSize: "14px",
            padding: "0.5rem 1rem",
            borderRadius: "10px",
          }}
        >
          <FaArrowLeft /> Back to Search
        </motion.button>
        <motion.div
          id="result-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            borderRadius: "32px",
            overflow: "hidden",
            border: `1px solid ${getBorderColor()}`,
            boxShadow: isDarkMode
              ? "0 25px 50px -12px rgba(0,0,0,0.5)"
              : "0 25px 50px -12px rgba(0,0,0,0.25)",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
              padding: "24px",
              color: "white",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  background: "rgba(255,255,255,0.2)",
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFileAlt size={18} color="white" />
              </div>
              <h2 style={{ marginBottom: "0", color: "white" }}>Results</h2>
            </div>
            <p
              style={{
                opacity: 0.9,
                marginLeft: "44px",
                color: "rgba(255,255,255,0.9)",
              }}
            >
              {result?.examMonth || "December"}-{result?.examYear || "2025"} /{" "}
              {result?.examMonth ? parseInt(result.examYear) + 1 : "2026"}
            </p>
          </div>
          <div style={{ padding: "24px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: "20px",
                marginBottom: "24px",
              }}
            >
              <div>
                <h1
                  style={{
                    fontSize: "28px",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    marginBottom: "8px",
                  }}
                >
                  {result?.name}
                </h1>
                <p style={{ color: getTextSecondary() }}>
                  <strong>USN:</strong> {result?.usn}
                </p>
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                gap: "16px",
                marginBottom: "24px",
                padding: "20px",
                background: isDarkMode ? "rgba(139,92,246,0.05)" : "#f8f9fa",
                borderRadius: "20px",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              <div style={{ textAlign: "center" }}>
                <p style={{ color: getTextSecondary(), fontSize: "12px" }}>
                  Overall Result
                </p>
                <p
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: overallResult === "PASS" ? "#10b981" : "#ef4444",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                >
                  {overallResult === "PASS" ? (
                    <FaCheckCircle />
                  ) : (
                    <FaTimesCircle />
                  )}
                  {overallResult}
                </p>
              </div>
              <div style={{ textAlign: "center" }}>
                <p style={{ color: getTextSecondary(), fontSize: "12px" }}>
                  Class Rank
                </p>
                <p
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {classRankValue !== undefined && classRankValue !== null
                    ? classRankValue
                    : "N/A"}
                </p>
              </div>
              <div style={{ textAlign: "center" }}>
                <p style={{ color: getTextSecondary(), fontSize: "12px" }}>
                  University Rank
                </p>
                <p
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {universityRankValue !== undefined &&
                  universityRankValue !== null
                    ? universityRankValue
                    : "N/A"}
                </p>
              </div>
            </div>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "bold",
                color: getTextColor(),
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaCalendarAlt color="#8b5cf6" /> Subject-wise Performance
            </h3>
            {sortedSemesters.map((semester) => (
              <div key={semester} style={{ marginBottom: "30px" }}>
                <h4
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    padding: "10px 16px",
                    borderRadius: "12px",
                    marginBottom: "12px",
                    fontSize: "16px",
                  }}
                >
                  Semester {semester} Subjects
                </h4>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "14px",
                      marginBottom: "20px",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: isDarkMode
                            ? "rgba(139,92,246,0.05)"
                            : "#f8f9fa",
                          borderBottom: `2px solid ${getBorderColor()}`,
                        }}
                      >
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            color: getTextColor(),
                          }}
                        >
                          Subject Code
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            color: getTextColor(),
                          }}
                        >
                          Subject Name
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          Internal
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          External
                        </th>
                        {result?.examType === "Revaluation" ? (
                          <>
                            <th
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                color: getTextColor(),
                              }}
                            >
                              Old Marks
                            </th>
                            <th
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                color: getTextColor(),
                              }}
                            >
                              Old Result
                            </th>
                            <th
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                background: "rgba(139,92,246,0.1)",
                                color: getTextColor(),
                              }}
                            >
                              RV Marks
                            </th>
                            <th
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                background: "rgba(139,92,246,0.1)",
                                color: getTextColor(),
                              }}
                            >
                              RV Result
                            </th>
                            <th
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                color: getTextColor(),
                              }}
                            >
                              Final Marks
                            </th>
                            <th
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                color: getTextColor(),
                              }}
                            >
                              Final Result
                            </th>
                          </>
                        ) : (
                          <>
                            <th
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                color: getTextColor(),
                              }}
                            >
                              Total
                            </th>
                            <th
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                color: getTextColor(),
                              }}
                            >
                              Result
                            </th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {groupedSubjects[semester].map((subject, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderBottom: `1px solid ${getBorderColor()}`,
                          }}
                        >
                          <td
                            style={{
                              padding: "12px",
                              fontFamily: "monospace",
                              color: "#8b5cf6",
                            }}
                          >
                            {subject.subjectCode}
                          </td>
                          <td
                            style={{ padding: "12px", color: getTextColor() }}
                          >
                            {subject.subjectName}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              color: getTextColor(),
                            }}
                          >
                            {subject.internal}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              color: getTextColor(),
                            }}
                          >
                            {subject.external}
                          </td>
                          {result?.examType === "Revaluation" ? (
                            <>
                              <td
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  color: getTextColor(),
                                }}
                              >
                                {subject.oldTotal !== undefined
                                  ? subject.oldTotal
                                  : subject.total || "—"}
                              </td>
                              <td
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  color:
                                    subject.oldResult === "P" ||
                                    (!subject.oldResult && subject.total >= 40)
                                      ? "#10b981"
                                      : "#ef4444",
                                }}
                              >
                                {subject.oldResult ||
                                  (subject.total >= 40 ? "P" : "F")}
                              </td>
                              <td
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  background: isDarkMode
                                    ? "rgba(139,92,246,0.1)"
                                    : "#fff5e6",
                                  color: subject.rvMarks ? "#8b5cf6" : "#999",
                                }}
                              >
                                {subject.rvMarks || "—"}
                              </td>
                              <td
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  background: isDarkMode
                                    ? "rgba(139,92,246,0.1)"
                                    : "#fff5e6",
                                  color:
                                    subject.rvResult === "P"
                                      ? "#10b981"
                                      : subject.rvResult === "F"
                                        ? "#ef4444"
                                        : "#999",
                                }}
                              >
                                {subject.rvResult || "—"}
                              </td>
                              <td
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  color: getTextColor(),
                                }}
                              >
                                {subject.finalTotal || subject.total}
                              </td>
                              <td
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  color:
                                    subject.finalResult === "P" ||
                                    (!subject.finalResult &&
                                      subject.total >= 40)
                                      ? "#10b981"
                                      : "#ef4444",
                                }}
                              >
                                {subject.finalResult || subject.result}
                              </td>
                            </>
                          ) : (
                            <>
                              <td
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  color: getTextColor(),
                                }}
                              >
                                {subject.total}
                              </td>
                              <td
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  color:
                                    subject.result === "P"
                                      ? "#10b981"
                                      : "#ef4444",
                                }}
                              >
                                {subject.result}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr
                        style={{
                          background: isDarkMode
                            ? "rgba(139,92,246,0.05)"
                            : "#f8f9fa",
                        }}
                      >
                        <td
                          colSpan="4"
                          style={{
                            padding: "12px",
                            textAlign: "right",
                            fontWeight: "bold",
                            color: getTextColor(),
                          }}
                        >
                          Semester Total:
                        </td>
                        <td
                          colSpan={
                            result?.examType === "Revaluation" ? "6" : "2"
                          }
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontWeight: "bold",
                            color: "#8b5cf6",
                          }}
                        >
                          {groupedSubjects[semester].reduce(
                            (sum, s) => sum + (s.finalTotal || s.total),
                            0,
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ))}
            <div
              className="action-buttons"
              style={{
                display: "flex",
                gap: "12px",
                marginTop: "24px",
                flexWrap: "wrap",
              }}
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={downloadResult}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  fontWeight: "500",
                }}
              >
                <FaDownload /> Save PDF
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={shareResult}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  fontWeight: "500",
                }}
              >
                <FaShare /> Share
              </motion.button>
              {result?.examType === "Regular" && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() =>
                    (window.location.href = `/leaderboard?usn=${result?.usn || ""}`)
                  }
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    fontWeight: "500",
                  }}
                >
                  <FaTrophy /> View Rankings
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
      <AnimatePresence>
        {showCelebration && clearedSubjects.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10000,
              backdropFilter: "blur(5px)",
            }}
            onClick={() => setShowCelebration(false)}
          >
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring", damping: 15 }}
              style={{
                background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
                borderRadius: "32px",
                padding: "2rem",
                maxWidth: "500px",
                width: "90%",
                textAlign: "center",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
                position: "relative",
                overflow: "hidden",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{
                  position: "absolute",
                  top: -50,
                  right: -50,
                  width: 200,
                  height: 200,
                  background:
                    "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
                  borderRadius: "50%",
                }}
              />
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 0.5, repeat: 3 }}
                style={{ fontSize: "4rem", marginBottom: "1rem" }}
              >
                🎉🎊✨
              </motion.div>
              <motion.h2
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", damping: 10 }}
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  marginBottom: "1rem",
                }}
              >
                Congratulations! 🎉
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                style={{
                  color: getTextSecondary(),
                  fontSize: "1rem",
                  marginBottom: "1rem",
                }}
              >
                You have cleared {clearedSubjects.length} backlog
                {clearedSubjects.length > 1 ? "s" : ""} in Revaluation!
              </motion.p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                style={{
                  background: isDarkMode
                    ? "rgba(139,92,246,0.1)"
                    : "rgba(139,92,246,0.05)",
                  borderRadius: "16px",
                  padding: "1rem",
                  marginBottom: "1.5rem",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                {clearedSubjects.map((subject, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.8 + idx * 0.1 }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "0.5rem 0",
                      borderBottom:
                        idx < clearedSubjects.length - 1
                          ? `1px solid ${getBorderColor()}`
                          : "none",
                    }}
                  >
                    <span style={{ fontSize: "0.9rem", color: getTextColor() }}>
                      {subject.subjectCode}
                    </span>
                    <span
                      style={{
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                        color: "#8b5cf6",
                      }}
                    >
                      {subject.oldTotal} → {subject.total}
                    </span>
                    <span style={{ color: "#10b981", fontSize: "0.8rem" }}>
                      ✓ Cleared!
                    </span>
                  </motion.div>
                ))}
              </motion.div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCelebration(false)}
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  padding: "0.8rem 1.5rem",
                  borderRadius: "40px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  cursor: "pointer",
                  boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                }}
              >
                Continue to Results 🚀
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResultChecker;
